/*
 * 作业客户端：页面与后端之间的唯一一层。
 *
 * 背景：后端（首尔那台的 FastAPI）还没写。但页面必须是一整页能从头走到尾的东西，
 *   而不是九张摆在一起的静态图，否则「上传之后会发生什么」永远没人验过。
 * 设计意图：先定契约，再给一个放录像的实现。事件名与阶段名照抄 PRD 第 6 章的
 *   SSE 事件（stage / diagnosis / awaiting_user / done / failed）与第 3.3 节的状态机，
 *   于是接后端时只需要把 startDemoJob 换成一个订阅 EventSource 的 startJob，
 *   页面那一层一行不用改。
 * 关键约束：
 *   1. 录像里的读数属于录像那张图，不属于用户刚上传的那张。页面必须把这件事说出来，
 *      本模块只负责在快照里带上 run 的出处，不负责替页面遮掩。
 *   2. 演示的节奏是压缩过的。真实耗时见 PRD 4.2（4K 诊断 25 到 35 秒，
 *      含一轮重画再加 60 到 300 秒），这里按秒级走完，只为让流程可点。
 *   3. 一切 setTimeout 都要能取消。用户换图或离开时留着的定时器会把已经作废的
 *      快照打回页面上，表现是「换了图还在跑上一张」。
 */
import type { Artifact, Diagnosis, RoundReading, RunStep } from '../types'
import type { PickError } from './pickImage'
import type { DemoRun } from '../mock/runs'
import { authHeaders } from './account'

/**
 * 页面看得见的作业阶段。
 *
 * 与 PRD 3.3 的服务端状态机对应关系：
 *   queued        → queued（票据已签、点数已预扣，等信号量）
 *   diagnosing    → running 的诊断阶段
 *   awaiting_confirm→ awaiting_user（gate=confirm）：诊断出来了，等用户点「下一步」
 *   awaiting_regen→ awaiting_user（gate=regen）：要花米粒那一道
 *   processing    → running / regenerating
 *   done / failed → done / failed
 *   expired       → expired（awaiting_user 15 分钟无响应，预扣退回）
 * 服务端还有 cancelled，页面暂时不给取消入口，所以这里没有。
 */
export type JobPhase =
  | 'queued'
  | 'diagnosing'
  | 'awaiting_confirm'
  | 'awaiting_regen'
  | 'processing'
  | 'done'
  | 'failed'
  | 'expired'

export interface JobSnapshot {
  id: string
  phase: JobPhase
  /**
   * 这份快照是放录像还是真作业。
   *
   * `?state=` 直达的十一屏仍然是录像（截图脚本与分享链接都在用它们），
   * 而真作业的读数属于用户自己那张图。页面靠这个标志决定要不要显示出处条：
   * 录像不声明出处，等于拿别人的数字冒充用户的结论。
   */
  demo?: boolean
  /** 服务端量出来的产物清单。录像态用 mock/runs.ts 里的那份。 */
  artifacts?: Artifact[]
  /**
   * 成品与原图的逐像素差，由服务端在**用户这张成品**上现算（PRD 1.4 第 4 条）。
   * 只切格子那一档才有意义（尺寸不变才量得出来），走过重生成的返回 null。
   */
  pixelDiff?: { maxLevels: number; meanLevels: number; changedPct: number } | null
  /** 这次重新生成的真实单价（米粒），来自换票时网关给的报价。 */
  regenPoints?: number | null
  /**
   * 换票时网关给的价目与免费额度。页面上所有的数字都从这里来，一个都不写死：
   * 单价在管理后台随时可改（capability_pricing），写死就会与线上不一致。
   */
  pricing?: {
    /** 基础档牌价（米粒）。免费那一趟也报它，用来划掉。 */
    restore: number
    /** 深度修复实收单价。 */
    regen: number
    /** 深度修复原价，只用来划掉；与实收相同或取不到时为 null。 */
    regenList: number | null
    /** 每天几次免费、今天还剩几次、这一趟用没用掉一次。 */
    freePerDay: number
    freeLeft: number
    freeApplied: boolean
  }
  /** 要摊开的步骤。处理中只给已经跑过的那几条，跑完给全部。 */
  steps: RunStep[]
  /** 最后一条是否还在跑。只有它能转圈（见 DecisionTrace）。 */
  running: boolean
  diag: Diagnosis | null
  after: Diagnosis | null
  /**
   * 每一轮的读数（0 = 处理前）。走过重画的作业才有两条以上。
   *
   * 「没修好」那一屏靠它把整条曲线摊出来：只报一个数字的话，「这类纹理压不动」
   * 与「只差一轮就过线」在屏上长得一模一样（2026-09-13 用户实撞，见 RoundCurve）。
   */
  rounds?: RoundReading[]
  error: PickError | null
  /**
   * 作业停在这个结局上的一句话说明，服务端已经换成中文（core/step_reason.py）。
   *
   * 「跑完了但没有成品」那一屏全靠它解释为什么——那一支不给 after，也没有成品，
   * 页面上除了这句话没有别的东西能说清发生了什么。
   */
  humanNote?: string
  /**
   * 换票时因为米粒不够，被降到只有净化那一档（网关回过 402）。
   *
   * 这张图如果真需要重画，作业会停在「交给人判断」上，而服务端给的说明是
   * 「要你先授权」——那一屏上并没有授权按钮，因为这张票根本没买重画。
   * 有这个标志，页面才能把话说对：不是等你点头，是米粒不够。
   */
  regenUnaffordable?: boolean
  /**
   * 这一屏的产物是从**上一次处理同一张图**抄来的（服务端按 sha256 认，见
   * store.find_reusable）。真值时页面必须说出来：这次没有重新处理、也没有扣米粒，
   * 不说的话用户会以为我们漏扣了他的钱，或者以为结果是假的。
   */
  cached?: boolean
  /**
   * 处理途中最新的那张图（完整地址）。每跑完一步换一张，页面把它模糊着渐变上来，
   * 于是几十秒的处理看得见在动，而不是一张原图挂到底。
   *
   * 它**不是成品**：中间图可能只清了一半，也可能是给模型看的参考图。作业一完成
   * 就该让位给 artifacts 里的成品（页面在 done 之后不再读它）。
   */
  previewSrc?: string | null
  /**
   * 产物的境内直取链接是什么时候领到的（毫秒时间戳）。
   *
   * 它本身不参与渲染，作用是**让完成屏重画**：领到链接时 artifactUrl 的返回值
   * 变了，而 React 只看快照变没变。没有这个字段，链接会一直躺在模块里没人用。
   */
  driveAt?: number
  /**
   * 这一趟**没**进盘，以及为什么（`quota_full` = 云端空间满了，其余归 `unknown`）。
   *
   * 只有问过米羊、而且它明确说没进，才会有值：录像态一次都不问，所以永远是 undefined。
   * 完成屏拿它换一句提醒——没进盘时首尔那份 24 小时后就清掉了。
   */
  driveMiss?: string | null
  /** awaiting_regen 的截止时刻（毫秒时间戳）。超时即 expired，预扣退回。 */
  holdExpiresAt: number | null
}

export interface JobHandle {
  /**
   * 诊断闸的「下一步」，对应 POST /api/jobs/{id}/continue。
   *
   * `authorizeRegen` 是「这一屏上已经把重画的价格摆出来、用户也点了同意」：
   * 那就连授权闸一起放行，不为同一件事停第二次。页面没摆过价格就传 false，
   * 真需要重画时授权闸照常拦下来。
   */
  proceed(authorizeRegen: boolean, deep?: boolean): void
  /** 授权重新生成，对应 POST /api/jobs/{id}/authorize-regen。 */
  authorizeRegen(): void
  /**
   * 松手：不再听这个作业，但让它在服务端接着跑。
   *
   * 页面卸载走这一条，**不是** dispose。作业现在活得比这一页长：地址栏里记着号，
   * 刷新、关标签页、明天再打开都接得回来（见 attachJob）。卸载就取消的话，
   * 刷新一下等于把一趟已经预扣过米粒的作业杀掉——正是 2026-09-11 撞见的那一幕：
   * 刷新之后作业变成 cancelled，页面上什么都没有了。
   */
  detach(): void
  /** 不要这个作业了：取消它（换一张图走这条），并收掉所有定时器。 */
  dispose(): void
}

/** 授权闸的有效期，PRD 3.3：awaiting_user 15 分钟无响应即 expired。 */
export const HOLD_MS = 15 * 60 * 1000

/**
 * 演示节奏。
 *
 * 真实耗时是这里的一到两个数量级（PRD 4.2），压缩是为了让人能一口气点完整条流程。
 * 页面上「已耗时」显示的是屏幕上真实过去的秒数，不假装成 60 秒，
 * 真实区间由文案说明（见「正在诊断」那一屏的 desc）。
 */
const MS = {
  queued: 420,
  diagnose: 2600,
  step: 1500,
  /** 重画那一步单独给长一点：它在真实作业里是唯一一个以分钟计的步骤。 */
  regenerate: 3200,
}

let seq = 0

/* ════════════════════════════════════════════════════════════════════════
 * 真作业
 *
 * 三步：广州换票 → 首尔上传 → 订 SSE。
 *   ① `POST /gateway/tools/ripple/ticket`（同源，nginx 转给网关 8001）
 *      鉴权、封禁、余额、预扣都发生在那一侧，回一张 600 秒的票据。
 *   ② `POST {API}/jobs`（multipart，图片 + 票据）打到首尔那台。
 *      **40 MB 的字节不经过广州**：那台机器同时在跑 /v1 流式推理，是收入路径。
 *   ③ `GET {API}/jobs/{id}/events` 订进度。事件名与阶段名与 PRD 第 6 章一致，
 *      也就是本文件下半段那卷录像用的同一套名字，所以页面那一层一行没改。
 *
 * 为什么 EventSource 不带鉴权：作业 id 是 `secrets.token_urlsafe`，不可枚举；
 * 而 EventSource 无法设请求头，为它另造一套 query token 只会多一个可泄漏的凭据。
 * ════════════════════════════════════════════════════════════════════════ */

/** 算力服务的前缀。页面挂在 /ripple/，所以是 /ripple/api——同源，无跨域。 */
const API = `${import.meta.env.BASE_URL}api`

/* ── 产物地址：能从盘上拿就不跨境 ──────────────────────────────────────────
 * 作业一结束，首尔会把这一趟的产物交进用户在广州的米羊盘
 * （lab_ripple_api/app/core/delivery.py → services/drive_intake.py）。交成了，
 * 广州按件签一条短时直取链接，浏览器直接向 COS 取：完成屏上原先两张 500 KB 的
 * 裁切各要 8 秒以上，那是一条跨境线路。
 *
 * 交不成的情形不少（访客没有盘、空间满了、首尔发不出去），所以这里只是**优先**：
 * 拿不到链接，下面所有地址照旧指首尔，完成屏一行都不用改。
 *
 * 链接两小时到期，而完成屏可能开着一整天。过了 MAX_AGE 就当没有——退回首尔
 * 那条慢路，好过在屏幕上摆一张打不开的图。真要下载时会重新领（见
 * artifactDownloadUrl）。
 */
interface DriveLinks {
  display: Record<string, string>
  download: Record<string, string>
  at: number
}
const driveLinks = new Map<string, DriveLinks>()
const LINKS_MAX_AGE = 90 * 60 * 1000

function freshLinks(jobId: string): DriveLinks | null {
  const hit = driveLinks.get(jobId)
  return hit && Date.now() - hit.at < LINKS_MAX_AGE ? hit : null
}

/**
 * 上一次问「进盘了没」得到的答案。
 *
 * 没进盘时要在完成屏上说出来：首尔那份产物 24 小时后会被清理任务删掉，而这一屏
 * 写着「之后回来还能下载」——那句话的前提就是已经进盘。`null` 是「进了」或者
 * 「还没问过」（录像态一次都不问）。
 */
const driveMiss = new Map<string, string>()

/**
 * 命中缓存的那一趟，产物在盘里挂在**被复用的那一趟**名下。
 *
 * 同一个人重传同一张图时，首尔不会把同样的十几兆再传一遍进盘——那只会在他的盘里
 * 多出一个一模一样的文件夹，并且再占一份额度（2026-09-12 用户裁决「复用上一趟
 * 那一组」，实现见 lab_ripple_api/app/jobs/runner.py 的 deliver_detached）。
 * 所以这一趟要领链接，得拿那一趟的作业号去问；链接仍按当前作业号存，页面无感。
 */
const cacheOrigin = new Map<string, string>()

async function loadDriveLinks(jobId: string): Promise<boolean> {
  const driveJob = cacheOrigin.get(jobId) || jobId
  try {
    const r = await fetch(`/api/ripple/artifacts?job=${encodeURIComponent(driveJob)}`,
                          { headers: authHeaders() })
    if (!r.ok) return false
    const d = await r.json()
    if (!d?.delivered) {
      driveMiss.set(jobId, String(d?.reason || 'unknown'))
      return false
    }
    driveMiss.delete(jobId)
    const display: Record<string, string> = {}
    const download: Record<string, string> = {}
    // 按**首尔那边的名字**建索引：盘里显示的是人话名（「诊断对照板（处理前）.png」），
    // 而这一页的产物清单来自首尔（`input_input_diag_board.png`）。按盘里的名字建，
    // 一条都对不上，等于这条路白铺。
    for (const f of (d.files ?? []) as
         { name: string; source_name?: string; display: string; download: string }[]) {
      const key = f.source_name || f.name
      if (f.display) display[key] = f.display
      if (f.download) download[key] = f.download
    }
    if (!Object.keys(display).length) return false
    driveLinks.set(jobId, { display, download, at: Date.now() })
    return true
  } catch {
    return false                                   // 取不到就继续走首尔
  }
}

/**
 * 点「下载」时用的地址。
 *
 * 和显示分两条：这一条带 `Content-Disposition: attachment`，浏览器会存盘而不是
 * 打开。而且这里**总是先确认链接还新鲜**——完成屏可能已经开了几个小时，
 * 那时躺着的链接早过期了，用户点下去只会拿到一张 403 的 XML。
 */
export async function artifactDownloadUrl(jobId: string, name: string): Promise<string> {
  if (!freshLinks(jobId)) await loadDriveLinks(jobId)
  return freshLinks(jobId)?.download[name] ?? `${API}/jobs/${jobId}/artifacts/${encodeURIComponent(name)}`
}

export const inputUrl = (jobId: string) => `${API}/jobs/${jobId}/input`
/** 原图的缩略图。历史列表的封面用它——没跑出成品的那几趟只有原图能认出是哪一次。 */
export const inputThumbUrl = (jobId: string) => `${API}/jobs/${jobId}/input?thumb=1`
/**
 * 原图的**同尺寸模糊占位图**，一两秒就到（原图跨境要几十秒，见 useSharpened）。
 * 尺寸与原图一致，所以换上原图时图框不跳、压在图上的红框也不用重算。
 */
export const inputSoftUrl = (jobId: string) => `${API}/jobs/${jobId}/input?placeholder=1`
export const artifactUrl = (jobId: string, name: string) =>
  freshLinks(jobId)?.display[name]
  ?? `${API}/jobs/${jobId}/artifacts/${encodeURIComponent(name)}`
/**
 * 同一个产物的缩略图（服务端现做、做完存着）。
 *
 * 轨迹里那些预览走这条：一趟深度修复挂七八张图，原尺寸加起来十几兆，
 * 为了看一眼预览把整包产物拖一遍是不划算的（2026-09-11「图片最好能直接看到预览」）。
 * 点开灯箱看的仍然是原图。
 */
export const artifactThumbUrl = (jobId: string, name: string) =>
  // 不能在 artifactUrl() 后面拼 `?thumb=1`：进盘后它是已经带 `?sign=…` 的 COS
  // 预签名 URL，再来一个问号会把签名拼坏，轨迹里的所有预览都变成破图。
  // 小图始终走首尔已有的缩略图端点；点开灯箱的原图仍由 artifactUrl() 走境内盘。
  `${API}/jobs/${jobId}/artifacts/${encodeURIComponent(name)}?thumb=1`

/**
 * 处理途中那张图的地址。作业跑完就没了，成品要走 artifactUrl。
 *
 * 后面缀第几步：重画之后还会再走一遍同样的工序，落的是**同一个文件名**（内容不同）。
 * 地址一样，页面就当是同一张图，既不重取也不过渡——画面停在上一轮的成品上。
 */
export const previewUrl = (jobId: string, name: string, n?: number) =>
  `${API}/jobs/${jobId}/preview/${encodeURIComponent(name)}${n ? `?n=${n}` : ''}`
/** 全部过程数据打成一个包，服务端边打边发（原图也在里面）。 */
export const bundleUrl = (jobId: string) => `${API}/jobs/${jobId}/bundle.zip`

/** 这一趟的报告（自包含 HTML，图内嵌，服务端现渲染，见 lab_ripple_api/app/report.py）。 */
export const reportUrl = (jobId: string) => `${API}/jobs/${jobId}/report.html`

/** 服务端错误信封与本地校验那份同形，所以页面渲染错误只有一段代码。 */
async function envelope(resp: Response): Promise<PickError> {
  try {
    const d = await resp.json()
    if (d?.message) return { code: String(d.code ?? 'error'), message: d.message, hint: d.hint ?? '' }
  } catch { /* 落到下面那句兜底 */ }
  return {
    code: 'network',
    message: '服务端没有正常回应，这次没有开始处理，也没有扣米粒。',
    hint: '过一会儿再试；一直这样请把这一页的时间点发给我们。',
  }
}

interface Ticket {
  ticket: string
  job_id: string
  is_guest: boolean
  stage: 'diagnose' | 'restore'
  allow_regen?: boolean
  /**
   * 价目表，不是这一趟收多少。`restore` 永远是基础档的牌价（免费那一趟也照报），
   * `regen_list` 是深度修复划掉的原价（2026-09-11：500 限时折 300），
   * 页面拿它们说「原价多少、现在多少」。这一趟实际预扣看 points_held。
   */
  price_points?: { restore: number; regen: number; regen_list?: number }
  /** 每天几次免费的基础处理（不含深度）。`applied` 是「这一趟用掉了一次」。 */
  free?: { per_day: number; left: number; applied: boolean }
}

/**
 * 换票。
 *
 * 先按「允许重新生成」问，因为预扣要按上限扣（诊断还没做，此时并不知道这张图
 * 需不需要重画）。余额只够净化那一档时网关回 402，这里**降级再问一次**：
 * 那张图很可能只需要切格子，不该因为「万一要重画」被挡在门外。
 * 降过级这件事必须记下来（`downgraded`）：那张图**真需要重画**时，作业会一路跑到
 * 「交给人判断」才停，而服务端只知道「这张票没有重画权限」，说出来的话是
 * 「要你先授权」——可那一屏上没有授权按钮，用户对着一句做不到的话没有下一步。
 * 只有这里知道真正的原因是米粒不够，所以由这里把它带到那一屏上去。
 */
async function getTicket(allowRegen: boolean, maxRegen: number):
  Promise<(Ticket & { downgraded: boolean }) | PickError> {
  const ask = async (regen: boolean) =>
    fetch('/gateway/tools/ripple/ticket', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ allow_regen: regen, max_regen: maxRegen }),
    })
  let resp = await ask(allowRegen)
  let downgraded = false
  if (resp.status === 402 && allowRegen) {
    downgraded = true
    resp = await ask(false)
  }
  if (!resp.ok) return envelope(resp)
  return { ...(await resp.json()) as Ticket, downgraded }
}

/**
 * 一台活作业的机器：状态、SSE、三颗按钮。
 *
 * 两个入口共用它——`startJob` 换票、上传、起一个新的；`attachJob` 接回一个已经存在的
 * （地址栏里带着作业号，刷新之后要能接着看，见 2026-09-11「URL 要变换，不然刷新就没了」）。
 * 分成两份写的话，SSE 那六个事件处理器就要各写一遍，早晚分叉。
 */
function liveJob(onSnap: (s: JobSnapshot) => void) {
  let dead = false
  let es: EventSource | null = null
  let jobId = ''
  let regenPoints: number | null = null
  let pricing: JobSnapshot['pricing']

  let snap: JobSnapshot = {
    id: '', phase: 'queued', steps: [], running: false,
    diag: null, after: null, error: null, holdExpiresAt: null,
  }
  const emit = (patch: Partial<JobSnapshot>) => {
    if (dead) return
    snap = { ...snap, ...patch }
    onSnap(snap)
  }
  const fail = (error: PickError) => emit({ phase: 'failed', running: false, error })

  /** 把服务端相位翻成页面相位。 */
  const phaseOf = (serverPhase: string): JobPhase => {
    switch (serverPhase) {
      case 'queued': return 'queued'
      case 'diagnosing': return 'diagnosing'
      case 'processing': return 'processing'
      case 'awaiting_confirm': return 'awaiting_confirm'
      case 'awaiting_regen': return 'awaiting_regen'
      case 'expired': return 'expired'
      case 'cancelled': return 'failed'
      case 'failed': return 'failed'
      default: return 'done'
    }
  }

  /** 领链接，领到了就让完成屏重画。 */
  const useDrive = async (): Promise<boolean> => {
    if (!jobId || dead) return false
    if (!await loadDriveLinks(jobId)) return false
    emit({ driveAt: Date.now(), driveMiss: null })
    return true
  }

  /**
   * 作业已经 done，可首尔没播 `drive` 那条。
   *
   * 交付发生在 done 之后，十几兆要传几秒——所以先等一等再问，不能立刻判它失败。
   * 三次都说没进，就把这件事摆到完成屏上：首尔那份产物 24 小时后会被清理任务删掉
   * （lab_ripple_api/app/jobs/cleanup.py），而那一屏写着「之后回来还能下载」。
   * 不说的话，用户第二天回来才发现东西没了。
   *
   * ⚠️ 报了「没进盘」之后还要接着问（2026-09-12 线上实跑发现）：快轮总共只有
   * 31 秒，而一趟四千万像素的图产物两三百兆，搬进盘要更久。停在那里等于把一句
   * 「没能存进你的米羊盘」永久留在屏上，而东西一分钟后其实进去了——那句话既
   * 是虚报，又没法自己收回。所以慢轮继续问到十分钟，进了就把话改回来；
   * 期间先按「没进」说，是因为催他现在下载是安全的那一侧。
   */
  let probing = false
  const probeDrive = async () => {
    if (probing || !jobId) return
    probing = true
    for (const wait of [3000, 8000, 20000]) {
      await new Promise(r => setTimeout(r, wait))
      if (dead) return
      if (await useDrive()) return
    }
    if (dead) return
    emit({ driveMiss: driveMiss.get(jobId) ?? 'unknown' })
    for (const wait of [30000, 60000, 60000, 120000, 120000, 180000]) {
      await new Promise(r => setTimeout(r, wait))
      if (dead) return
      if (await useDrive()) return
    }
  }

  const applySnapshot = (d: any) => {
    // 命中缓存的那一趟：产物挂在被复用的那一趟名下，领链接要拿那个作业号去问。
    // 必须在下面两条问盘之前登记，否则第一次问的还是自己的号，白问一趟。
    if (d.cached_from) cacheOrigin.set(d.id ?? jobId, String(d.cached_from))
    // 接回来的那一趟（刷新、隔天再打开）在快照里就带着交付结果，
    // 那条 drive 事件早在页面打开之前就播过了。
    if (d.drive && !freshLinks(d.id ?? jobId)) void useDrive()
    else if (!d.drive && !d.running && phaseOf(d.phase) === 'done') void probeDrive()
    emit({
      id: d.id ?? jobId,
      phase: phaseOf(d.phase),
      steps: (d.steps ?? []) as RunStep[],
      running: !!d.running,
      diag: d.diagnosis ?? snap.diag,
      after: d.diagnosis_after ?? snap.after,
      rounds: d.rounds?.length ? (d.rounds as RoundReading[]) : snap.rounds,
      artifacts: d.artifacts ?? snap.artifacts,
      pixelDiff: normalizeDiff(d.pixel_diff) ?? snap.pixelDiff,
      holdExpiresAt: d.hold_expires_at ? d.hold_expires_at * 1000 : null,
      error: d.error ?? null,
      humanNote: d.human_note || snap.humanNote,
      cached: !!d.cached_from || snap.cached,
      previewSrc: d.preview ? previewUrl(jobId, d.preview) : snap.previewSrc,
      regenPoints,
      // 价目跟着快照一起带上：接回来的那一趟（attachJob）没换过票，
      // 价钱是从标签页里捡回来的，不在这儿铺就到不了闸上。
      pricing: pricing ?? snap.pricing,
    })
  }

  const listen = () => {
    es = new EventSource(`${API}/jobs/${jobId}/events`)
    es.addEventListener('snapshot', e => applySnapshot(JSON.parse((e as MessageEvent).data)))
    es.addEventListener('stage', e => {
      const d = JSON.parse((e as MessageEvent).data)
      const steps = [...snap.steps.filter(s => s.n !== d.step.n), d.step as RunStep]
        .sort((a, b) => a.n - b.n)
      emit({ steps, running: !!d.running, phase: phaseOf(d.phase) })
    })
    es.addEventListener('preview', e => {
      const d = JSON.parse((e as MessageEvent).data)
      if (d.name) emit({ previewSrc: previewUrl(jobId, d.name, d.n) })
    })
    es.addEventListener('diagnosis', e => {
      const d = JSON.parse((e as MessageEvent).data)
      // round ≥ 1 是重画之后的复诊，不是「处理前」。收下它会让完成页那一列
      // 「处理前」的数字变成重画出来那张图的读数（服务端同一处已经收口，
      // 见 runner.py 的 diagnosis 分支；这一路是 SSE 直推，得各自挡一次）。
      if ((d.round ?? 0) > 0) return
      emit({ diag: d.diagnosis as Diagnosis })
    })
    es.addEventListener('awaiting_user', e => {
      // 两道闸同一个事件，由 gate 分辨（诊断闸 2026-09-11 加）。旧事件没有这个
      // 字段，按授权闸算——那时只有这一道。
      const d = JSON.parse((e as MessageEvent).data)
      emit({
        phase: d.gate === 'confirm' ? 'awaiting_confirm' : 'awaiting_regen',
        running: false,
        holdExpiresAt: d.hold_expires_at * 1000,
      })
    })
    es.addEventListener('done', e => {
      applySnapshot(JSON.parse((e as MessageEvent).data))
      es?.close()
    })
    // 交付发生在作业结束**之后**，所以它是 done 之后才来的一条。
    // 领到链接就重画一次完成屏，那几张图与下载按钮随之改指境内。
    es.addEventListener('drive', () => { void useDrive() })
    es.addEventListener('failed', e => {
      fail(JSON.parse((e as MessageEvent).data) as PickError)
      es?.close()
    })
    es.onerror = () => {
      // 连接断了不等于作业断了（重生成那一步可以静默几分钟）。
      // 拉一次全量快照：作业还活着就接着等，已经结束就按结果收尾。
      if (dead) return
      fetch(`${API}/jobs/${jobId}`).then(r => r.json()).then(d => {
        applySnapshot(d)
        if (['done', 'failed', 'cancelled', 'expired'].includes(d.phase)) es?.close()
      }).catch(() => { /* 下一次 onerror 再试 */ })
    }
  }

  const handle: JobHandle = {
    proceed(authorizeRegen: boolean, deep = false) {
      if (!jobId) return
      // deep 是用户在这一屏上主动加选的深度修复。它一定连着授权走：这一步要花钱，
      // 服务端也只认「授权过的」那种（见 app/api/jobs.py 的 continue_job）。
      void fetch(`${API}/jobs/${jobId}/continue`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ authorize_regen: authorizeRegen || deep, deep }),
      })
      // 不等回包就切屏：这一跳只是写个哨兵文件，而页面停在「诊断完成」上
      // 等一个来回，看起来像按钮没反应。真出错了，SSE 会把它切回来。
      emit({ holdExpiresAt: null, phase: 'queued', running: true })
    },
    authorizeRegen() {
      if (!jobId) return
      void fetch(`${API}/jobs/${jobId}/authorize-regen`, { method: 'POST' })
      emit({ holdExpiresAt: null, phase: 'processing', running: true })
    },
    detach() {
      dead = true
      es?.close()
    },
    dispose() {
      dead = true
      es?.close()
      if (jobId) void fetch(`${API}/jobs/${jobId}/cancel`, { method: 'POST' }).catch(() => {})
    },
  }

  return {
    handle, emit, fail, listen, applySnapshot,
    get dead() { return dead },
    setJob(id: string) { jobId = id },
    setPricing(p: JobSnapshot['pricing'], regen: number | null) {
      pricing = p
      regenPoints = regen
    },
  }
}

/**
 * 起一个真作业。换票、订 SSE、错误处理都在这儿，**图从哪来**由 `fill` 那一行决定：
 * 这次选的文件（startJob），或者上一趟还在服务器上的那张原图（rerunJob）。
 *
 * 两条路共用这一段，是因为它们唯一的差别就是 FormData 里那一个字段。分开写的话，
 * 定价、降档、失败信封、SSE 这四样早晚有一处只改了一边。服务端那一侧同理，
 * 见 app/api/jobs.py 的 create_job。
 */
function beginJob(
  loggedIn: boolean,
  maxRegen: number,
  onSnap: (s: JobSnapshot) => void,
  fill: (form: FormData) => void,
): JobHandle {
  const j = liveJob(onSnap)

  void (async () => {
    const t = await getTicket(loggedIn, maxRegen)
    if (j.dead) return
    if ('code' in t) return j.fail(t)
    j.setJob(t.job_id)
    const regenPoints = t.price_points?.regen ?? null
    const list = t.price_points?.regen_list ?? null
    const pricing: JobSnapshot['pricing'] = {
      restore: t.price_points?.restore ?? 0,
      regen: regenPoints ?? 0,
      regenList: list != null && regenPoints != null && list > regenPoints ? list : null,
      freePerDay: t.free?.per_day ?? 0,
      freeLeft: t.free?.left ?? 0,
      freeApplied: !!t.free?.applied,
    }
    j.setPricing(pricing, regenPoints)
    rememberPricing(t.job_id, pricing, regenPoints)
    if (t.downgraded) j.emit({ regenUnaffordable: true })

    const form = new FormData()
    fill(form)
    form.append('ticket', t.ticket)
    form.append('allow_regen', t.allow_regen ? '1' : '0')
    form.append('max_regen', String(maxRegen))
    const resp = await fetch(`${API}/jobs`, { method: 'POST', body: form })
    if (j.dead) return
    if (!resp.ok) return j.fail(await envelope(resp))
    j.emit({ id: t.job_id, phase: 'queued', regenPoints, pricing })
    j.listen()
  })()

  return j.handle
}

/**
 * @param file       用户那张图，已经过 pickImage 的本地校验
 * @param loggedIn   登录用户才拿得到净化档；访客的票据只到诊断
 * @param onSnap     每次推进回调一次完整快照，形状与录像那卷完全相同
 */
export function startJob(
  file: File,
  loggedIn: boolean,
  onSnap: (s: JobSnapshot) => void,
  maxRegen = 1,
): JobHandle {
  return beginJob(loggedIn, maxRegen, onSnap, form => form.append('image', file, file.name))
}

/**
 * 把上一趟那张图**重新跑一遍**，不用用户再选一次文件。
 *
 * 2026-09-12 用户裁决：「这个不是重新处理吗？为什么还要重新上传啊」。半路停下的
 * 那几屏（等超时、中途失败）此前给的是「重新上传这张图」——可那张原图还在服务器上，
 * 作业目录留 24 小时（见 app/api/jobs.py 的 `_prior_input`），让人回去自己电脑上
 * 翻文件是我们的账没记清。
 *
 * 这是一趟**新作业**，不是把旧的那趟捡起来接着跑：旧的那趟的预扣早就退回去了
 * （等超时会退，见 runner 的收尾），所以要重新换票、重新预扣、拿一个新作业号。
 * 页面地址栏也会跟着换成新号（onSnap 那一侧写的），旧号仍然能打开、仍然是那句
 * 「这一趟等超时了」——两趟是两件事，不该互相覆盖。
 *
 * 原图过了 24 小时被清掉时，服务端回 `input_gone`，页面照常显示那句话，
 * 把用户领回上传那条路。
 */
export function rerunJob(
  fromJob: string,
  loggedIn: boolean,
  onSnap: (s: JobSnapshot) => void,
  maxRegen = 1,
): JobHandle {
  return beginJob(loggedIn, maxRegen, onSnap, form => form.append('from_job', fromJob))
}

/**
 * 接着上一趟**重画出来的那张候选图**再重画一轮（「再来一轮」）。
 *
 * 2026-09-13 用户裁决。重画一趟最多两轮（服务端把 max_regen 掐在 2），撞上限停下来
 * 的那一趟常常只差一轮：同一张图实测 36.4% → 29.4% → 11.4% → 3.5%，第三轮就过线了。
 * 从原图重跑（rerunJob）会把前两轮的算力和米粒再烧一遍，而且出图有随机性，未必
 * 还能落回同一个位置——所以这一条接的是上一趟的产物，不是上一趟的原图。
 *
 * 与 rerunJob 一样是**新作业**：新换票、新预扣、新作业号。区别只在服务端拿哪张图
 * 当输入（`continue_from` vs `from_job`，见 app/api/jobs.py 的 create_job），
 * 以及这一趟不再停闸——按钮上摆过价钱、用户按了，就是授权。
 */
export function continueRound(
  fromJob: string,
  loggedIn: boolean,
  onSnap: (s: JobSnapshot) => void,
): JobHandle {
  return beginJob(loggedIn, 1, onSnap, form => form.append('continue_from', fromJob))
}

/**
 * 接回一个已经存在的作业：地址栏里带着作业号，刷新之后接着看。
 *
 * 背景：2026-09-11 用户在诊断那一屏上说「URL 要变换，不然刷新就没了」。在那之前
 *   地址栏自始至终是 /ripple/，而作业号只活在这一页的内存里——手滑刷新一下，
 *   一张正在跑的图、一道等着他决定的闸、甚至已经扣过米粒的成品，全都找不回来，
 *   页面还退回到「选一张图」，像什么都没发生过。
 * 设计意图：先拉一份全量快照（跑完的那些由服务端从盘上重放），没跑完再订 SSE 接着看。
 * 关键约束：
 *   1. 作业号就是凭据：它是 `secrets.token_urlsafe`，不可枚举，产物接口一直是按它
 *      认人的（见本文件头那段）。所以它进地址栏等于这一趟作业可以被转发——
 *      这是有意的（用户自己那张图的结论，他要能存书签、能发给人看），
 *      但也意味着它和产物链接一样，24 小时后随作业一起消失。
 *   2. 取不回来就说清楚，不要装作没这回事：作业过了 24 小时会被清掉，
 *      那时页面要把地址栏那截也一并清掉，刷新回到「选一张图」。
 *   3. 价格是换票时网关给的，作业本身不记；同一个标签页刷新能从 sessionStorage
 *      拿回来（见 rememberPricing），换个标签页打开就只剩「单价待定」那条路——
 *      闸上本来就有这个标记，它说的是实话。
 */
export function attachJob(jobId: string, onSnap: (s: JobSnapshot) => void): JobHandle {
  const j = liveJob(onSnap)
  j.setJob(jobId)
  const known = recallPricing(jobId)
  if (known) j.setPricing(known.pricing, known.regenPoints)
  j.emit({ id: jobId, phase: 'queued', running: true,
           pricing: known?.pricing, regenPoints: known?.regenPoints ?? null })

  void (async () => {
    let d: any
    try {
      const resp = await fetch(`${API}/jobs/${jobId}`)
      if (!resp.ok) throw new Error(String(resp.status))
      d = await resp.json()
    } catch {
      if (j.dead) return
      return j.fail({
        code: 'job_gone',
        message: '处理记录已过期',
        hint: '作业记录保留 24 小时，请重新上传图像。',
      })
    }
    if (j.dead) return
    j.applySnapshot(d)
    // 取消掉的那一趟服务端不留失败原因（它不是失败），而页面的失败屏全靠那句话
    // 说明发生了什么——没有它就是一屏空白。这里补上。
    if (d.phase === 'cancelled') {
      return j.fail({
        code: 'job_cancelled',
        message: '任务已取消',
        hint: '预扣额度已退还，可点击下方按钮重新发起处理。',
      })
    }
    if (!['done', 'failed', 'expired'].includes(d.phase)) j.listen()
  })()

  return j.handle
}

/**
 * 这一趟的价目，按作业号记在标签页里。
 *
 * 只为刷新这一件事：价格是换票时网关给的，作业那边不记（它只知道自己被预扣了多少）。
 * 用 sessionStorage 而不是 localStorage——价目会变，隔天再打开不该拿旧价说话；
 * 拿不到就走「单价待定」，闸上那个标记说的是实话，不许编一个数字充数。
 */
const PRICE_KEY = 'ripple.pricing.'

function rememberPricing(jobId: string, pricing: JobSnapshot['pricing'],
                         regenPoints: number | null): void {
  try {
    sessionStorage.setItem(PRICE_KEY + jobId, JSON.stringify({ pricing, regenPoints }))
  } catch { /* 无痕模式之类的，拿不到就算了 */ }
}

function recallPricing(jobId: string):
  { pricing: JobSnapshot['pricing']; regenPoints: number | null } | null {
  try {
    const raw = sessionStorage.getItem(PRICE_KEY + jobId)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function normalizeDiff(d: any) {
  if (!d) return null
  return { maxLevels: d.max_levels, meanLevels: d.mean_levels, changedPct: d.changed_pct }
}

/**
 * 放一卷录像。
 *
 * @param run       放哪一卷（两条终局路径各一卷）
 * @param onSnap    每次阶段推进回调一次完整快照，形状与 GET /api/jobs/{id} 一致
 * @param from      从哪儿开始放。`?state=` 直达某一屏之后按下去的按钮要接着能动，
 *                  否则那几屏是死的，而「按下去会怎样」正是合成整页要验的东西。
 */
export function startDemoJob(
  run: DemoRun,
  onSnap: (s: JobSnapshot) => void,
  from: 'upload' | 'diagnosed' | 'authorized' = 'upload',
): JobHandle {
  const id = `demo-${++seq}`
  const timers: number[] = []
  let dead = false

  let snap: JobSnapshot = {
    id,
    phase: 'queued',
    demo: true,
    steps: [],
    running: false,
    diag: null,
    after: null,
    error: null,
    holdExpiresAt: null,
  }

  const emit = (patch: Partial<JobSnapshot>) => {
    if (dead) return
    snap = { ...snap, ...patch }
    onSnap(snap)
  }
  const later = (ms: number, fn: () => void) => {
    if (dead) return
    timers.push(window.setTimeout(fn, ms))
  }

  /** 处理阶段：从 processFrom 开始，一条一条揭示，最后一条揭示完即 done。 */
  const runSteps = (from: number) => {
    const reveal = (i: number) => {
      const step = run.steps[i]
      emit({ phase: 'processing', steps: run.steps.slice(0, i + 1), running: true })
      const wait = step.action === 'regenerate' ? MS.regenerate : MS.step
      later(wait, () => {
        if (i + 1 < run.steps.length) reveal(i + 1)
        else emit({ phase: 'done', steps: run.steps, running: false, after: run.after })
      })
    }
    reveal(from)
  }

  /**
   * 诊断做完之后停在诊断闸。
   * 要花米粒的那一档，价格就摆在诊断闸这一屏上，点了「下一步」等于连授权一起给了；
   * 不要花钱的那一档，同一屏上只有「下一步」。
   */
  const afterDiagnosis = () => {
    // 真作业那边第一停一律是诊断闸（gate=confirm）：要不要花米粒由**这一屏的内容**
    // 决定，不由相位决定（见 RipplePage 那个 case 的注释）。录像照同一个形状走，
    // 否则截图里的流程与真走一遍不是同一条。
    emit({ phase: 'awaiting_confirm', steps: [], running: false, holdExpiresAt: Date.now() + HOLD_MS })
    later(HOLD_MS, () => emit({ phase: 'expired', holdExpiresAt: null }))
  }

  if (from === 'upload') {
    later(MS.queued, () => {
      emit({ phase: 'diagnosing', steps: [run.diagnoseStep], running: true })
      later(MS.diagnose, () => {
        emit({ diag: run.before })
        afterDiagnosis()
      })
    })
    onSnap(snap)
  } else {
    snap = { ...snap, diag: run.before }
    if (from === 'diagnosed') afterDiagnosis()
    else runSteps(run.processFrom)
  }

  return {
    proceed() {
      if (snap.phase !== 'awaiting_confirm' && snap.phase !== 'awaiting_regen') return
      emit({ holdExpiresAt: null })
      runSteps(run.processFrom)
    },
    authorizeRegen() {
      if (snap.phase !== 'awaiting_regen') return
      emit({ holdExpiresAt: null })
      runSteps(run.processFrom)
    },
    // 录像没有服务端那一半，松手与丢弃是同一件事。
    detach() {
      dead = true
      timers.forEach(clearTimeout)
    },
    dispose() {
      dead = true
      timers.forEach(clearTimeout)
    },
  }
}

/**
 * 直接把作业摆到某个阶段，不走时间线。
 *
 * 背景：九个状态里有几个很难自然复现——余额不足、授权超时、失败。逐屏截图
 *   （scripts/shoot_playground.sh）也不可能等着定时器走完。
 * 设计意图：URL 上的 ?state= 就落到这里，得到的快照与真跑到那一步时的快照同形，
 *   所以页面渲染的是同一份代码，不存在「截图上好看、真走一遍不一样」。
 */
export function seedSnapshot(phase: JobPhase, run: DemoRun): JobSnapshot {
  const base: JobSnapshot = {
    id: 'seed',
    phase,
    demo: true,
    steps: [],
    running: false,
    diag: null,
    after: null,
    error: null,
    holdExpiresAt: null,
  }
  switch (phase) {
    case 'diagnosing':
      return { ...base, steps: [run.diagnoseStep], running: true }
    case 'awaiting_confirm':
    case 'awaiting_regen':
    case 'expired':
      return {
        ...base,
        diag: run.before,
        holdExpiresAt: phase === 'expired' ? null : Date.now() + HOLD_MS,
      }
    case 'processing':
      return {
        ...base,
        diag: run.before,
        steps: run.steps.slice(0, Math.min(3, run.steps.length)),
        // 录像里没有真的过程图，拿这一档的成品充数：这一屏要看的是「图在变、
        // 模糊着渐变过来」这件事，而它与真作业走的是同一段渲染。
        previewSrc: run.images?.afterFull ?? null,
        running: true,
      }
    case 'done':
      return { ...base, diag: run.before, after: run.after, steps: run.steps }
    case 'failed':
      // 中途断掉的那一趟，诊断早就跑完了——它是第一步，而这一屏现在照实把标红图与
      // 读数留着（2026-09-12 用户裁决，见 RipplePage 的 Stopped）。录像里不带这份诊断，
      // 演示出来的就是另一屏，而这个文件存在的理由正是「两边同形」。
      return { ...base, diag: run.before, steps: [run.diagnoseStep], error: JOB_FAILED_EXAMPLE }
    default:
      return base
  }
}

/**
 * 拒收那一屏的样例错误（?state=failed）。
 *
 * 直接复用 pickImage 会生成的那一段，只是把数字填成 5120×2880（常见的 5K 壁纸）。
 * 用户真拖一张 5K 图进来时看到的是同一段文案，不是为截图另写的一份——
 * 两份文案分头改是这类页面最容易出的错。
 */
export const OVER_CAP_EXAMPLE: PickError = {
  code: 'too_many_pixels',
  message:
    '你这张是 5120×2880（14.75 MP），超过 8.3 MP 的上限。' +
    '我们按原始像素做全幅分析，不会替你缩放：要检测的纹理周期只有 2 到 4 像素，' +
    '一旦重采样就会先被抹掉，缩放后的图必然被判成干净的。',
  hint: '请自己裁切或另存到上限以内再上传，裁切不影响诊断——纹理是全幅均匀的。',
}

/**
 * 作业中途失败那一屏的样例错误（?state=job_failed）。
 *
 * 与拒收是两回事：拒收发生在动手之前，什么都没花；这一条发生在钱已经预扣、
 * 活干到一半的时候，所以必须说清楚钱怎么办（PRD 5.3：失败的预扣必须退回）。
 * 取重画超时作例子是因为它是唯一一个以分钟计的外部依赖（PRD 3.2：读超时 300 到 600 秒）。
 * ⚠️ code 字符串接后端时要与服务端返回的对齐，现在这个是占位。
 */
const JOB_FAILED_EXAMPLE: PickError = {
  code: 'decode_failed',
  message:
    '重新生成那一步等了 600 秒仍然没有返回，作业已经中止。你的原图没有被改动，' +
    '这次预扣的米粒已经退回你的余额。',
  hint: '出图接口偶尔会堵，隔几分钟重试一次通常就过去了；连续失败请把这一页的时间点发给我们。',
}
